import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Divider,
  Slider,
  FormHelperText,
  CircularProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  useCreateAgentMutation,
  useUpdateAgentMutation
} from '../../../store/slices/apiSlice';

const AgentForm = ({ open, onClose, mode = 'create', agent = null }) => {
  const [createAgent, { isLoading: isCreating }] = useCreateAgentMutation();
  const [updateAgent, { isLoading: isUpdating }] = useUpdateAgentMutation();
  const [apiError, setApiError] = useState('');

  const isEdit = mode === 'edit';
  const isLoading = isCreating || isUpdating;

  // Schéma de validation Yup
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Le nom est requis')
      .min(2, 'Le nom doit contenir au moins 2 caractères')
      .max(100, 'Le nom ne peut pas dépasser 100 caractères'),
    description: Yup.string()
      .max(500, 'La description ne peut pas dépasser 500 caractères'),
    type: Yup.string()
      .oneOf(['whatsapp', 'telegram', 'discord', 'web'], 'Type invalide')
      .required('Le type est requis'),
    'configuration.model': Yup.string()
      .oneOf(['gpt-4', 'gpt-3.5-turbo', 'claude-3', 'gemini-pro'], 'Modèle invalide')
      .required('Le modèle est requis'),
    'configuration.temperature': Yup.number()
      .min(0, 'La température doit être supérieure ou égale à 0')
      .max(2, 'La température doit être inférieure ou égale à 2')
      .required('La température est requise'),
    'configuration.maxTokens': Yup.number()
      .min(1, 'Le nombre de tokens doit être supérieur à 0')
      .max(8000, 'Le nombre de tokens ne peut pas dépasser 8000')
      .required('Le nombre de tokens est requis'),
    'configuration.systemPrompt': Yup.string()
      .required('Le prompt système est requis')
      .min(10, 'Le prompt système doit contenir au moins 10 caractères'),
    'configuration.welcomeMessage': Yup.string()
      .max(200, 'Le message de bienvenue ne peut pas dépasser 200 caractères'),
    'configuration.fallbackMessage': Yup.string()
      .max(200, 'Le message de secours ne peut pas dépasser 200 caractères')
  });

  // Configuration Formik
  const formik = useFormik({
    initialValues: {
      name: agent?.name || '',
      description: agent?.description || '',
      type: agent?.type || 'whatsapp',
      configuration: {
        model: agent?.configuration?.model || 'gpt-3.5-turbo',
        temperature: agent?.configuration?.temperature || 0.7,
        maxTokens: agent?.configuration?.maxTokens || 1000,
        systemPrompt: agent?.configuration?.systemPrompt || '',
        welcomeMessage: agent?.configuration?.welcomeMessage || '',
        fallbackMessage: agent?.configuration?.fallbackMessage || '',
        apiKeys: {
          openai: agent?.configuration?.apiKeys?.openai || '',
          claude: agent?.configuration?.apiKeys?.claude || '',
          gemini: agent?.configuration?.apiKeys?.gemini || ''
        }
      }
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setApiError('');

        if (isEdit) {
          await updateAgent({
            id: agent.id,
            data: values
          }).unwrap();
        } else {
          await createAgent(values).unwrap();
        }

        onClose();
        formik.resetForm();
      } catch (error) {
        setApiError(error.data?.message || 'Une erreur est survenue lors de la sauvegarde');
      }
    }
  });

  // Reset du formulaire à la fermeture
  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setApiError('');
    }
  }, [open]);

  const handleClose = () => {
    onClose();
    formik.resetForm();
    setApiError('');
  };

  // Options pour les sélecteurs
  const typeOptions = [
    { value: 'whatsapp', label: 'WhatsApp', icon: '💬' },
    { value: 'telegram', label: 'Telegram', icon: '📱' },
    { value: 'discord', label: 'Discord', icon: '🎮' },
    { value: 'web', label: 'Web', icon: '🌐' }
  ];

  const modelOptions = [
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo', provider: 'OpenAI' },
    { value: 'gpt-4', label: 'GPT-4', provider: 'OpenAI' },
    { value: 'claude-3', label: 'Claude 3', provider: 'Anthropic' },
    { value: 'gemini-pro', label: 'Gemini Pro', provider: 'Google' }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <SettingsIcon color="primary" />
          <Typography variant="h6">
            {isEdit ? 'Modifier l\'agent' : 'Créer un nouvel agent'}
          </Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {apiError}
            </Alert>
          )}

          <Grid container spacing={3}>

            {/* Informations de base */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Informations de base
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom de l'agent"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={formik.touched.type && Boolean(formik.errors.type)}>
                <InputLabel>Type d'agent</InputLabel>
                <Select
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Type d'agent"
                >
                  {typeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <span>{option.icon}</span>
                        <span>{option.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.type && formik.errors.type && (
                  <FormHelperText>{formik.errors.type}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
                multiline
                rows={3}
                placeholder="Décrivez brièvement le rôle et les fonctionnalités de cet agent..."
              />
            </Grid>

            {/* Configuration IA */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Configuration IA
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={formik.touched['configuration.model'] && Boolean(formik.errors['configuration.model'])}>
                <InputLabel>Modèle IA</InputLabel>
                <Select
                  name="configuration.model"
                  value={formik.values.configuration.model}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Modèle IA"
                >
                  {modelOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.provider}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched['configuration.model'] && formik.errors['configuration.model'] && (
                  <FormHelperText>{formik.errors['configuration.model']}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre de tokens max"
                name="configuration.maxTokens"
                type="number"
                value={formik.values.configuration.maxTokens}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched['configuration.maxTokens'] && Boolean(formik.errors['configuration.maxTokens'])}
                helperText={formik.touched['configuration.maxTokens'] && formik.errors['configuration.maxTokens']}
                InputProps={{ inputProps: { min: 1, max: 8000 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                Température: {formik.values.configuration.temperature}
              </Typography>
              <Slider
                name="configuration.temperature"
                value={formik.values.configuration.temperature}
                onChange={(_, value) => formik.setFieldValue('configuration.temperature', value)}
                min={0}
                max={2}
                step={0.1}
                marks={[
                  { value: 0, label: 'Précis' },
                  { value: 1, label: 'Équilibré' },
                  { value: 2, label: 'Créatif' }
                ]}
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Prompt système"
                name="configuration.systemPrompt"
                value={formik.values.configuration.systemPrompt}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched['configuration.systemPrompt'] && Boolean(formik.errors['configuration.systemPrompt'])}
                helperText={formik.touched['configuration.systemPrompt'] && formik.errors['configuration.systemPrompt']}
                multiline
                rows={4}
                placeholder="Définissez le comportement et la personnalité de votre agent..."
                required
              />
            </Grid>

            {/* Configuration avancée */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Configuration avancée</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Message de bienvenue"
                        name="configuration.welcomeMessage"
                        value={formik.values.configuration.welcomeMessage}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched['configuration.welcomeMessage'] && Boolean(formik.errors['configuration.welcomeMessage'])}
                        helperText={formik.touched['configuration.welcomeMessage'] && formik.errors['configuration.welcomeMessage']}
                        multiline
                        rows={2}
                        placeholder="Salut ! Je suis votre assistant..."
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Message de secours"
                        name="configuration.fallbackMessage"
                        value={formik.values.configuration.fallbackMessage}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched['configuration.fallbackMessage'] && Boolean(formik.errors['configuration.fallbackMessage'])}
                        helperText={formik.touched['configuration.fallbackMessage'] && formik.errors['configuration.fallbackMessage']}
                        multiline
                        rows={2}
                        placeholder="Désolé, je n'ai pas compris..."
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            startIcon={<CloseIcon />}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={isLoading || !formik.isValid}
          >
            {isLoading ? 'Sauvegarde...' : (isEdit ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AgentForm;
